/**
 * GMU CookieBanner — Shopify ecommerce-event tracker.
 *
 * Schiet GA4-spec dataLayer-events op user-acties die niet via page-load
 * detecteerbaar zijn:
 *   - select_item        (klik op product-tile in collection / featured)
 *   - view_item (re-fire) (variant-keuze op PDP)
 *   - add_to_cart        (POST naar /cart/add, of een hoger aantal via
 *                         /cart/change of /cart/update)
 *   - remove_from_cart   (een lager aantal of verwijderde regel via
 *                         /cart/change of /cart/update)
 *
 * Werkt op alle Shopify Online Store 2.0 themes (Dawn, Sense, Refresh,
 * custom Liquid). Theme-agnostisch: hookt via fetch-wrap op de Cart-AJAX-
 * API, niet op DOM-selectors.
 *
 * Voor Shopify Checkout (Plus/2024+ Checkout Extensibility) zijn
 * add_shipping_info/add_payment_info/purchase via Web Pixels te tracken.
 * Standaard storefront-pages dekken we hier.
 */
(function () {
    'use strict';

    const ctx = window.SHOP_CONTEXT || {};
    const dl  = (window.dataLayer = window.dataLayer || []);
    const currency = ctx.shop?.currency || 'EUR';
    const tracking = ctx.tracking || {};

    // GTM merget elke push recursief in zijn datamodel (arrays per index).
    // Zonder reset erft een event de items van een eerder ecommerce-event,
    // bv. een remove_from_cart van 1 regel de overige regels van view_cart.
    function push(event, payload) {
        dl.push({ ecommerce: null });
        dl.push({ event, ecommerce: payload });
    }

    // ---------- user_context + consent-gated email_sha256 -----------------
    // window.SHOP_USER wordt door de Liquid-snippets gezet (of is een
    // thenable) — zie snippets/gmu-user-context.liquid. Ongated user_context
    // altijd pushen; user_data (gehasht e-mailadres) alleen na marketing-
    // consent via window.ShopBanner.ready.
    function pushUserContext(user) {
        if (!user) return;
        dl.push({
            event: 'user_context',
            user_logged_in:   !!user.logged_in,
            customer_type:    user.customer_type || 'unknown',
            customer_id_hash: user.customer_id_hash || null
        });
        if (user.email_sha256) {
            whenBannerReady(function (state) {
                if (state && state.choices && state.choices.marketing === true) {
                    dl.push({ event: 'user_data', user_data: { sha256_email_address: user.email_sha256 } });
                }
            });
        }
    }

    // Gedeeld fragment — GEEN los script. Wordt door scripts/sync-shared-js.mjs
    // ingevoegd op de plek van `// @include ../shared/banner-ready.js` in
    // tracker-core/tracker.js en shopify-events/events.js. Zo staat de logica op
    // één plek zonder dat de adapters een extra request hoeven te doen.
    //
    // Bewust ES5-stijl (var + function): het fragment landt in twee bestanden met
    // verschillende conventies en moet in allebei passen.
    //
    // Waarom dit bestaat: banner.js laadt met `async`, dus consumers kunnen eerder
    // draaien dan de banner. Een ontbrekende window.ShopBanner betekent dan "nog
    // niet geladen", niet "geen banner" — meteen opgeven zou events buiten het
    // consent-venster om versturen (tracker) of de Enhanced-Conversions-set
    // stilzwijgend laten vervallen (Shopify).

    // Hoe lang we op een banner wachten die zich na `load` nog moet melden.
    var BANNER_WAIT_MS = 3000;

    function whenBannerReady(cb) {
        var b = window.ShopBanner;
        if (b && b.ready && typeof b.ready.then === 'function') { b.ready.then(cb); return; }

        // Staat er geen banner-script in de pagina, dan komt er ook geen
        // consent-signaal en heeft wachten geen zin. Bewust op "banner.js" en niet
        // op "banner": thema's zitten vol met hero-banner.js en banner-slider.js,
        // en daarop wachten zou de meting nodeloos ophouden. Dekt alle bronnen —
        // gmu-banner.js bij de adapters, banner.js bij de GTM-injectie.
        if (!document.querySelector('script[src*="banner.js"]')) {
            if (document.readyState !== 'loading') cb(null);
            else document.addEventListener('DOMContentLoaded', function () { cb(null); });
            return;
        }

        var done = false;
        function once(state) { if (!done) { done = true; cb(state); } }

        document.addEventListener('shopbanner:loaded', function () {
            var banner = window.ShopBanner;
            if (banner && banner.ready && typeof banner.ready.then === 'function') banner.ready.then(once);
            else once(null);
        }, { once: true });

        // Vangnet voor een banner die stuk of geblokkeerd is: dan liever alsnog
        // meten dan helemaal niet. `load` is het signaal zolang de pagina nog
        // laadt; draaien we daarna (GTM-injectie, late bundle) dan komt dat event
        // niet meer en valt de timeout in.
        if (document.readyState === 'complete') setTimeout(function () { once(null); }, BANNER_WAIT_MS);
        else window.addEventListener('load', function () { once(null); }, { once: true });
    }

    Promise.resolve(window.SHOP_USER || null).then(pushUserContext);

    /**
     * Bouwt een item_id uit het per-shop geconfigureerde template
     * (ctx.tracking.id_format), identiek aan de server-side helper
     * snippets/gmu-item-id.liquid. Default '{sku}'. Placeholders:
     * {country} {product} {variant} {sku} {gtin}. Valt terug op product
     * als de ingevulde template leeg zou zijn.
     */
    function buildItemId(parts) {
        const fmt = tracking.id_format || '{sku}';
        const sub = (k) => (parts[k] != null && parts[k] !== '' ? String(parts[k]) : '');
        const land    = tracking.feed_country || '';
        const product = sub('product');
        // Twee placeholder-conventies, allebei ondersteund zodat geen enkele
        // bestaande klantconfiguratie breekt. {landcode}/{product_id}/
        // {productid} is wat WooCommerce, PrestaShop en Magento gebruiken;
        // {country}/{product}/{variant} was de eigen conventie van de Shopify
        // theme-adapter. Volgorde doet er niet toe — het zijn losse tokens.
        //
        // replaceAll (niet replace) zodat een format met een herhaalde
        // placeholder identiek aan de server-side Liquid-snippet uitpakt —
        // Liquid `replace` vervangt ALLE occurrences. Zie tests/item-id-parity.
        const id = fmt
            .replaceAll('{landcode}',   land)
            .replaceAll('{country}',    land)
            .replaceAll('{product_id}', product)
            .replaceAll('{productid}',  product)
            .replaceAll('{product}',    product)
            .replaceAll('{variant}',    sub('variant'))
            .replaceAll('{sku}',        sub('sku'))
            .replaceAll('{gtin}',       sub('gtin'));
        return id || product;
    }

    // CommonJS-export voor unit tests (geen effect in browser; daar is `module`
    // undefined). Exporteert de pure item_id-helper zodat een parity-test 'm
    // kan vergelijken met de server-side snippet (snippets/gmu-item-id.liquid).
    if (typeof module !== 'undefined' && typeof module.exports === 'object') {
        module.exports = { buildItemId };
    }

    /**
     * Map een Shopify cart-line of cart-item naar een GA4-item-shape.
     * Cart-API returnt prijzen in centen — delen door 100.
     */
    function mapShopifyItem(item) {
        return {
            item_id:       buildItemId({ product: item.product_id, variant: item.variant_id || item.id, sku: item.sku, gtin: item.barcode }),
            item_name:     String(item.product_title || item.title || ''),
            item_category: String(item.product_type || ''),
            price:         (Number(item.price) || 0) / 100,
            quantity:      Number(item.quantity) || 1,
            currency,
            product_id:    String(item.product_id || item.id),
            quote_item_id: String(item.key || item.id),
        };
    }

    // ---------- select_item: klik op product-tile in lijst ----------------
    // Shopify product-tiles variëren per theme. We luisteren breed op
    // <a href> binnen elementen met `.product-card` / `.card-product` /
    // `[data-product-id]` markup. Dawn-thema gebruikt <a class="card-link">
    // in card-component.
    document.addEventListener('click', (e) => {
        const link = e.target.closest(
            'a.product-card-link, a.card-link, a.product-link, ' +
            '.product-card a[href*="/products/"], .card-product a[href*="/products/"], ' +
            '[data-product-id] a[href*="/products/"]'
        );
        if (!link) return;

        const tile = link.closest('.product-card, .card-product, [data-product-id], [data-product-handle]') || link;
        const items = ctx.items || [];

        // Match-strategieën in volgorde:
        //  1. data-product-id attribute matches ctx.items[].product_id
        //  2. handle uit href matches niets... fallback op 1e item
        const productId = tile.dataset?.productId
                       || tile.dataset?.productHandle
                       || extractProductIdFromHref(link.getAttribute('href'));
        if (productId) {
            const m = items.find(i => String(i.product_id) === String(productId)
                                   || i.item_id === String(productId));
            if (m) {
                push('select_item', {
                    item_list_name: m.item_list_name || '',
                    items: [m],
                });
                return;
            }
        }

        // Fallback: 1e tile-occurrence in lijst pakken (index-positie)
        const allTiles = Array.from(document.querySelectorAll(
            '.product-card, .card-product, [data-product-id]'
        ));
        const idx = allTiles.indexOf(tile);
        if (idx >= 0 && items[idx]) {
            push('select_item', {
                item_list_name: items[idx].item_list_name || '',
                items: [items[idx]],
            });
        }
    }, true);

    function extractProductIdFromHref(href) {
        if (!href) return null;
        // /products/handle?variant=12345 of /products/handle
        const m = href.match(/\/products\/([^/?#]+)/);
        return m ? m[1] : null;
    }

    // ---------- view_item RE-FIRE bij variant-keuze (configurable) -------
    // Shopify themes updaten doorgaans `?variant=ID` in de URL bij swatch-
    // klik (window.history.replaceState) of dispatchen een custom event.
    // We luisteren op popstate + custom 'variant:change'-events + URL-
    // mutatie via MutationObserver op de form's hidden input.
    function fireViewItemForVariantId(variantId) {
        if (!variantId || ctx.page?.type !== 'product' || ctx.product?.type !== 'configurable') return;
        const variant = ctx.product.variants?.[variantId];
        if (!variant) return;
        push('view_item', {
            currency,
            value: variant.price ?? ctx.product.price,
            items: [{
                ...ctx.product,
                item_id:    variant.item_id,
                price:      variant.price,
                product_id: String(variantId),
                variant_of: ctx.product.item_id,
            }],
        });
    }

    // Patroon 1: hidden input mutatie via change-event
    document.addEventListener('change', (e) => {
        const input = e.target;
        if (input?.tagName === 'INPUT' && (input.name === 'id' || input.name === 'variant_id')) {
            fireViewItemForVariantId(input.value);
        }
        // Dawn: variant-radios met data-option-id (swatches)
        if (input?.type === 'radio' && input.closest('variant-radios, variant-selects')) {
            // De form's hidden #ProductSelect-* input wordt geüpdated; wacht
            // 1 tick zodat de Dawn-variant-script eerst draait.
            setTimeout(() => {
                const formInput = document.querySelector('form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] input[name="variant_id"]');
                if (formInput) fireViewItemForVariantId(formInput.value);
            }, 30);
        }
    }, true);

    // Patroon 2: theme-developer hook — analoog aan Magento's gmu:variant-changed
    document.addEventListener('gmu:variant-changed', (e) => {
        fireViewItemForVariantId(e.detail?.variant_id || e.detail?.sku);
    });

    // ---------- AJAX cart-events via fetch-wrap ---------------------------
    // Shopify gebruikt /cart/add, /cart/change, /cart/update (met of zonder
    // `.js`, eventueel onder een locale-prefix). We wrappen window.fetch en
    // leiden de events af uit de cart vóór en ná elke mutatie — niet uit de
    // request-body, want themes adresseren regels verschillend (`line` als
    // 1-based regelnummer, `id` als key of variant-id) en SHOP_CONTEXT.items
    // bevat buiten de /cart-pagina geen cart-regels.
    // Themes die de cart via XMLHttpRequest muteren vallen hierbuiten.
    const CART_ADD_RE    = /\/cart\/add(\.js|\?|$)/;
    const CART_CHANGE_RE = /\/cart\/change(\.js|\?|$)/;
    const CART_UPDATE_RE = /\/cart\/update(\.js|\?|$)/;
    const CART_STATE_RE  = /\/cart\.js(on)?(\?|$)/;

    const origFetch = window.fetch?.bind(window);
    if (origFetch) {
        const snapshot = createCartSnapshot(origFetch);
        snapshot.refresh();

        window.fetch = async function (input, opts) {
            const url = typeof input === 'string' ? input : (input?.url || '');
            const isAdd  = CART_ADD_RE.test(url);
            const isDiff = CART_CHANGE_RE.test(url) || CART_UPDATE_RE.test(url);
            // Zonder vóór-stand valt er niets te diffen; alleen dan wachten.
            if (isAdd || isDiff) await snapshot.ready();

            const resp = await origFetch(input, opts);

            if (isAdd || isDiff || CART_STATE_RE.test(url)) {
                try {
                    // Clone zodat de consumer de body nog kan lezen.
                    handleCartResponse(snapshot, isAdd, isDiff, await resp.clone().json());
                } catch { /* response was geen JSON */ }
            }
            if (isAdd || isDiff) {
                // Triggert ook persistTrackingIds() → cart-attributen verversen
                // na elke add/change/update (eigen /cart/update.js valt via de
                // lastSig-guard in een no-op, dus geen lus).
                document.dispatchEvent(new CustomEvent('gmu:cart-changed'));
            }
            return resp;
        };
    }

    function handleCartResponse(snapshot, isAdd, isDiff, data) {
        if (isAdd) {
            // /cart/add returnt alleen de toegevoegde regel(s), geen cart, met
            // als quantity het nieuwe totaal van die regel. Het toegevoegde
            // aantal is dus het verschil met de vóór-stand.
            const added = (Array.isArray(data?.items) ? data.items : [data]).filter(i => i && i.key);
            const before = snapshot.items();
            added
                .map(item => ({ item, delta: qty(item) - qty(before?.get(item.key)) }))
                .filter(c => c.delta > 0)
                .forEach(c => pushDelta('add_to_cart', [c]));
            snapshot.merge(added);
            return;
        }
        if (!isFullCart(data)) return;
        if (isDiff) pushCartDiff(diffCart(snapshot.items(), data.items));
        snapshot.set(data);
    }

    function isFullCart(data) {
        return !!data && Array.isArray(data.items) && data.item_count != null;
    }

    function qty(item) {
        return Number(item?.quantity) || 0;
    }

    /**
     * Verschil per regel-`key` tussen twee cart-standen. Levert de regels met
     * hun quantity-delta: positief = erbij, negatief = eraf. Een verdwenen
     * regel komt terug met de oude regel en minus zijn oude aantal.
     */
    function diffCart(before, afterItems) {
        if (!before) return [];
        const changes = [];
        const seen = new Set();
        afterItems.forEach(item => {
            seen.add(item.key);
            const delta = qty(item) - qty(before.get(item.key));
            if (delta !== 0) changes.push({ item, delta });
        });
        before.forEach((item, key) => {
            if (!seen.has(key)) changes.push({ item, delta: -qty(item) });
        });
        return changes;
    }

    function pushCartDiff(changes) {
        pushDelta('add_to_cart',      changes.filter(c => c.delta > 0));
        pushDelta('remove_from_cart', changes.filter(c => c.delta < 0));
    }

    function pushDelta(event, changes) {
        if (!changes.length) return;
        const items = changes.map(c => mapShopifyItem({ ...c.item, quantity: Math.abs(c.delta) }));
        push(event, {
            currency,
            value: items.reduce((sum, i) => sum + i.price * i.quantity, 0),
            items,
        });
    }

    /**
     * Laatst bekende cart-stand (regels per key). Bij page-load opgehaald via
     * de originele fetch, zodat de wrap zijn eigen request niet ziet; daarna
     * bijgewerkt uit elke volledige cart-response en uit de regels die
     * /cart/add teruggeeft. Een refresh die terugkomt nadat een mutatie de stand al
     * heeft gezet, is verouderd en wordt genegeerd.
     */
    function createCartSnapshot(fetchCart) {
        const root = window.Shopify?.routes?.root || '/';
        let items = null;
        let pending = null;
        let generation = 0;

        function set(cart) {
            generation++;
            items = new Map(cart.items.map(i => [i.key, i]));
        }

        // Regels uit /cart/add erbij of eroverheen. Zonder bekende stand valt
        // er niets aan te vullen; de volgende mutatie haalt hem dan op.
        function merge(lines) {
            if (!items) return;
            generation++;
            lines.forEach(i => items.set(i.key, i));
        }

        function refresh() {
            items = null;
            const startedAt = ++generation;
            const request = fetchCart(root + 'cart.js', { credentials: 'same-origin' })
                .then(r => r.json())
                .then(cart => { if (generation === startedAt && isFullCart(cart)) set(cart); })
                .catch(() => { /* geen vóór-stand; een volgende mutatie probeert opnieuw */ })
                .finally(() => { if (pending === request) pending = null; });
            pending = request;
            return request;
        }

        function ready() {
            if (items) return Promise.resolve();
            return pending || refresh();
        }

        return { set, merge, refresh, ready, items: () => items };
    }

    // ---------- Generic theme hook ---------------------------------------
    // Shopify themes verschillen flink in hoe ze cart-events naar de
    // frontend communiceren. Voor downstream tools dispatchen we een
    // stabiele `gmu:cart-changed` event na elke cart-mutation.
    document.addEventListener('cart:update', () => {
        document.dispatchEvent(new CustomEvent('gmu:cart-changed'));
    });
    // Dawn-thema pubsub equivalent
    document.addEventListener('cart-update', () => {
        document.dispatchEvent(new CustomEvent('gmu:cart-changed'));
    });

    // ---------- GA4/Ads-identifiers → cart-attributen --------------------
    // De Shopify Checkout-pixel (Customer Events) draait in een sandbox en
    // kan de storefront-cookies (_ga / _ga_<stream> / _gcl_*) niet
    // betrouwbaar lezen. Daarom leggen we client_id, session_id en de
    // Google-klik-IDs hier vast — waar de cookies wél leesbaar zijn — als
    // cart-attributen. Die reizen mee naar de checkout en komen in de pixel
    // binnen via `checkout.attributes`, zodat de purchase aan dezelfde
    // GA4-sessie/bron blijft hangen (geen Session source/medium = "(not set)"
    // meer, en geen dubbele random client_ids bij re-fire).
    (function persistTrackingIds() {
        function readCookie(name) {
            const safe = name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1');
            const m = document.cookie.match('(?:^|; )' + safe + '=([^;]*)');
            return m ? decodeURIComponent(m[1]) : '';
        }
        // client_id uit _ga  (GA1.1.<cid1>.<cid2>  ->  "<cid1>.<cid2>")
        function gaClientId() {
            const m = readCookie('_ga').match(/GA\d\.\d\.(\d+\.\d+)/);
            return m ? m[1] : '';
        }
        // session_id uit _ga_<stream>. Stream optioneel via
        // SHOP_CONTEXT.tracking.ga4_stream, anders auto-detect 1e _ga_*-cookie.
        // Ondersteunt beide GA4-cookieformaten:
        //   GS1.1.<sid>.<n>...       (ouder)
        //   GS2.1.s<sid>$o1$g1$...   (nieuwer, $-gescheiden, 's'-prefix)
        function gaSessionId() {
            const stream = tracking.ga4_stream;
            let val = stream ? readCookie('_ga_' + stream) : '';
            if (!val) {
                const m = document.cookie.match(/_ga_([A-Z0-9]+)=GS\d\.\d\.s?\d+/);
                if (m) val = readCookie('_ga_' + m[1]);
            }
            const sm = val.match(/GS\d\.\d\.s?(\d+)/);
            return sm ? sm[1] : '';
        }

        function collect() {
            const a = {};
            const cid = gaClientId();   if (cid) a['_ga_cid'] = cid;
            const sid = gaSessionId();  if (sid) a['_ga_sid'] = sid;
            const aw = readCookie('_gcl_aw'); if (aw) a['_gcl_aw'] = aw;
            const dc = readCookie('_gcl_dc'); if (dc) a['_gcl_dc'] = dc;
            const au = readCookie('_gcl_au'); if (au) a['_gcl_au'] = au;
            try {
                const q = new URLSearchParams(location.search);
                const gclid = q.get('gclid') || q.get('wbraid') || q.get('gbraid') || '';
                if (gclid) a['_ga_gclid'] = gclid;
            } catch (e) { /* ignore */ }
            return a;
        }

        let lastSig = '';
        function persist() {
            const attrs = collect();
            if (!attrs['_ga_cid']) return;        // gtag nog niet geladen → later opnieuw
            const sig = JSON.stringify(attrs);
            if (sig === lastSig) return;          // niets veranderd → geen extra request
            lastSig = sig;
            try {
                // De fetch-wrap hierboven onderschept /cart/update.js, maar
                // alleen attributen wijzigen geeft een lege cart-diff (geen
                // ecommerce-event, geen lus) — dus veilig om te gebruiken.
                fetch('/cart/update.js', {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({ attributes: attrs }),
                    keepalive: true,
                }).catch(() => {});
            } catch (e) { /* ignore */ }
        }

        // 1) bij page-load: blijf proberen tot gtag _ga heeft gezet (na consent)
        let tries = 0;
        (function tryPersist() {
            if (gaClientId()) { persist(); return; }
            if (tries++ < 8) setTimeout(tryPersist, 1500);   // ~12s venster voor consent
        })();
        // 2) vlak vóór de checkout (klik op checkout-knop / form-submit)
        document.addEventListener('click', (e) => {
            if (e.target.closest('[name="checkout"], a[href*="/checkout"], button[href*="/checkout"]')) persist();
        }, true);
        document.addEventListener('submit', (e) => {
            const action = e.target?.getAttribute?.('action') || '';
            if (/\/cart|\/checkout/.test(action)) persist();
        }, true);
        // 3) na elke cart-mutatie (nieuw cart-token kan attributen resetten)
        document.addEventListener('gmu:cart-changed', persist);
    })();
})();
